// A button that opens a list of actions (`MenuButton`), and a button that
// opens a list of values to choose one from (`SelectMenu`).
//
// Promise (both): the list opens on click, Arrow keys move between items
// (wrapping), Enter or a click runs one and closes the list, Escape closes it.
// Focus goes to the first item (the chosen one, for a value) on open from the
// keyboard (to the list when opened by the pointer) and back to the button on
// close. `Menu` is the action list for a button drawn by someone else (a
// round button on the phone's Dock). A menu's `footer` (the account's theme
// and language) sits under its items; choosing there keeps the list open.
//
// The native `<select>` is not used: the OS draws its list, which the app's
// theme, corners and rows cannot reach, and it differs on every device.
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";
import { Caret, Icon, type IconKind } from "./icon";
import { Popover } from "./popover";

export interface MenuItem {
  id: string;
  label: string;
  onSelect(): void;
  danger?: boolean;
  disabled?: boolean;
  /**
   * A mark at the row's start. When any item of a menu has one, the others
   * keep its room, so every label starts at the same place.
   */
  icon?: IconKind;
}

/** Separators have no identity of their own: name them after their position. */
const keyed = (items: readonly (MenuItem | "separator")[]) =>
  items.map((item, index) => ({
    item,
    key: item === "separator" ? `separator-${index}` : item.id,
  }));

/**
 * Moves the focus among the enabled items of `list` (0: the first). A choice
 * in the footer is one stop: its chosen segment.
 */
function focusItemIn(list: HTMLElement | null, step: 1 | -1 | 0) {
  const all = [
    ...(list?.querySelectorAll<HTMLButtonElement>(
      "[role=menuitem]:not(:disabled), [role=switch]:not(:disabled), [role=radio][aria-checked=true]:not(:disabled)",
    ) ?? []),
  ];
  if (all.length === 0) return;
  const at = all.indexOf(document.activeElement as HTMLButtonElement);
  const next = step === 0 ? 0 : (at + step + all.length) % all.length;
  all[next]?.focus();
}

/**
 * The list of a menu, floating at `anchor`. Whoever draws the anchor opens it
 * (and calls `focusMenuOnOpen` to move the focus in as `MenuButton` does).
 */
export function Menu({
  anchor,
  open,
  onClose,
  label,
  items,
  align = "start",
  listRef,
  footer,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose(): void;
  label: string;
  items: readonly (MenuItem | "separator")[];
  align?: "start" | "end";
  listRef?: RefObject<HTMLDivElement | null>;
  /** Under the items, after a separator. */
  footer?: ReactNode;
}) {
  const own = useRef<HTMLDivElement>(null);
  const list = listRef ?? own;
  const marked = items.some((i) => i !== "separator" && i.icon);
  return (
    <Popover anchor={anchor} open={open} onClose={onClose} align={align}>
      <div
        ref={list}
        role="menu"
        aria-label={label}
        tabIndex={-1}
        className="outline-none"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            focusItemIn(list.current, 1);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            focusItemIn(list.current, -1);
          }
        }}
      >
        {keyed(items).map(({ item, key }) =>
          item === "separator" ? (
            <hr key={key} className="my-1 h-px border-0 bg-border-soft" />
          ) : (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              title={item.label}
              disabled={item.disabled}
              className={`flex h-8 w-full cursor-pointer items-center gap-2.5 px-3 text-left text-[13px] hover:bg-hover focus:bg-hover focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                item.danger ? "text-danger" : "text-body"
              }`}
              onClick={() => {
                anchor.current?.focus();
                onClose();
                item.onSelect();
              }}
            >
              {marked &&
                (item.icon ? (
                  <Icon
                    kind={item.icon}
                    className={item.danger ? "" : "text-muted"}
                  />
                ) : (
                  <span aria-hidden="true" className="w-4 flex-none" />
                ))}
              {/* One line each: a long label (a Case's title) is cut, never wrapped over the next row. */}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </button>
          ),
        )}
        {footer && (
          <>
            <hr className="my-1 h-px border-0 bg-border-soft" />
            {footer}
          </>
        )}
      </div>
    </Popover>
  );
}

/** After opening a menu: the first item from the keyboard, the list from the pointer. */
export function focusMenuOnOpen(
  list: RefObject<HTMLDivElement | null>,
  byKeyboard: boolean,
) {
  // Opened from the keyboard (no pointer: detail 0), the first item takes the
  // focus and its ground; opened by the pointer, the list does, so no row
  // looks chosen before the pointer is on one (the old web's). Arrow keys
  // reach the items either way.
  requestAnimationFrame(() =>
    byKeyboard ? focusItemIn(list.current, 0) : list.current?.focus(),
  );
}

export function MenuButton({
  label,
  items,
  align = "start",
  className = "",
  footer,
  children,
}: {
  /** Read aloud for the button. */
  label: string;
  items: readonly (MenuItem | "separator")[];
  align?: "start" | "end";
  className?: string;
  /** Under the items (see `Menu`). */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const list = useRef<HTMLDivElement>(null);
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={className}
        onClick={(e) => {
          setOpen((v) => !v);
          focusMenuOnOpen(list, e.detail === 0);
        }}
      >
        {children}
      </button>
      <Menu
        anchor={button}
        open={open}
        onClose={close}
        label={label}
        items={items}
        align={align}
        listRef={list}
        footer={footer}
      />
    </>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Drawn faint (a value that means "none": the CLI's default, no directory). */
  muted?: boolean;
  /** Before the label in the list (a state's mark, a person's avatar). */
  icon?: ReactNode;
}

/**
 * The default look of a `SelectMenu`'s button: a framed one-line field of a
 * settings row (the same frame as `ROW_INPUT`), the value and a caret.
 */
export const SELECT_TRIGGER =
  "flex min-h-[34px] w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-[7px] border border-field bg-surface px-[10px] text-left text-[13px] text-fg outline-none hover:border-field-strong focus-visible:border-field-strong disabled:cursor-not-allowed disabled:opacity-50";

/** How tall the list grows before it scrolls (a long list: time zones). */
const SELECT_LIST_MAX = 320;
/** Typed letters within this long are one search (a time zone's name). */
const TYPEAHEAD_MS = 700;

/**
 * Chooses one value from a list: a button showing the chosen value that opens
 * the list under it, the chosen value checked. Choosing calls `onChange` and
 * closes it. Typing letters moves to the first value that starts with them.
 */
export function SelectMenu<T extends string>({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  className = SELECT_TRIGGER,
  align = "start",
  trigger,
  listClassName,
}: {
  /** Read aloud for the button and the list. */
  label: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange(value: T): void;
  /** Shown when no option has the value (an action like "Add a member"). */
  placeholder?: string;
  disabled?: boolean;
  /** The button's look (default: a settings row's field). */
  className?: string;
  align?: "start" | "end";
  /**
   * What the button shows instead of the chosen value and the caret (a pill,
   * a detail's property row). The list stays the same.
   */
  trigger?: ReactNode;
  /** The list's width (default: at least the button's). */
  listClassName?: string;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const typed = useRef({ text: "", at: 0 });
  const listId = useId();
  const current = options.find((o) => o.value === value);
  const rows = () => [
    ...(list.current?.querySelectorAll<HTMLButtonElement>("[role=option]") ??
      []),
  ];
  const focusAt = (index: number) => {
    const all = rows();
    if (all.length === 0) return;
    const row = all[(index + all.length) % all.length];
    row?.focus();
    row?.scrollIntoView({ block: "nearest" });
  };
  const focusStep = (step: 1 | -1) => {
    const all = rows();
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    focusAt(at < 0 ? (step > 0 ? 0 : all.length - 1) : at + step);
  };
  const choose = (next: T) => {
    button.current?.focus();
    setOpen(false);
    if (next !== value) onChange(next);
  };
  const typeahead = (key: string) => {
    const now = Date.now();
    const text =
      (now - typed.current.at < TYPEAHEAD_MS ? typed.current.text : "") +
      key.toLowerCase();
    typed.current = { text, at: now };
    const index = options.findIndex((o) =>
      o.label.toLowerCase().startsWith(text),
    );
    if (index >= 0) focusAt(index);
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        className={className}
        onClick={(e) => {
          setOpen((v) => !v);
          const byKeyboard = e.detail === 0;
          const chosen = Math.max(
            0,
            options.findIndex((o) => o.value === value),
          );
          // The chosen value is in sight either way; from the keyboard it
          // takes the focus, by the pointer the list does (no row looks
          // hovered before the pointer is on one).
          requestAnimationFrame(() => {
            if (byKeyboard) focusAt(chosen);
            else {
              list.current?.focus();
              rows()[chosen]?.scrollIntoView({ block: "nearest" });
            }
          });
        }}
      >
        {trigger ?? (
          <>
            <span
              className={`min-w-0 flex-1 truncate ${
                !current || current.muted ? "text-faint" : ""
              }`}
            >
              {current?.label ?? placeholder ?? ""}
            </span>
            {/* Always pointing down: the list being out says it is open. */}
            <span className="flex-none text-[10px] text-faint">
              <Caret open />
            </span>
          </>
        )}
      </button>
      <Popover
        anchor={button}
        open={open}
        onClose={close}
        align={align}
        matchWidth={listClassName === undefined}
        maxHeight={SELECT_LIST_MAX}
        className={listClassName}
      >
        <div
          ref={list}
          id={listId}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          className="outline-none"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              focusStep(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              focusStep(-1);
            } else if (e.key === "Home") {
              e.preventDefault();
              focusAt(0);
            } else if (e.key === "End") {
              e.preventDefault();
              focusAt(-1);
            } else if (
              e.key.length === 1 &&
              e.key !== " " &&
              !e.metaKey &&
              !e.ctrlKey &&
              !e.altKey
            ) {
              e.preventDefault();
              typeahead(e.key);
            }
          }}
        >
          {options.map((o) => {
            const chosen = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={chosen}
                className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] hover:bg-hover focus:bg-hover focus:outline-none ${
                  o.muted ? "text-faint" : "text-body"
                }`}
                onClick={() => choose(o.value)}
              >
                {o.icon !== undefined && (
                  <span className="flex flex-none items-center">{o.icon}</span>
                )}
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {/* The mark's box is always there: the label never moves. */}
                <span className="flex w-3 flex-none justify-center text-accent">
                  {chosen && <Icon kind="check" size={12} />}
                </span>
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
